import { FileRepository } from "@/components/features";
import { AppModal, Button, LoadingSpinner } from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterFileSchema } from "@/lib/data/schemas";
import { type Matter, type MatterFile } from "@/lib/types";
import { Suspense, useCallback, useEffect, useState } from "react";
interface AddOrEditFileReferenceSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matter?: Matter;
  handleCreateMatterFileReference: (selectedFiles: MatterFile[]) => void;
  isCreating: boolean;
  autoOpenNewFileUpload?: boolean;
}

export const matterFileReferenceFormSchema = MatterFileSchema.pick({
  id: true,
  description: true,
});

export const AddOrEditFileReferenceSlideover = ({
  open,
  setOpen,
  matter,
  handleCreateMatterFileReference,
  isCreating,
  autoOpenNewFileUpload,
}: AddOrEditFileReferenceSlideoverProps) => {
  const [selectedFiles, setSelectedFiles] = useState<MatterFile[]>([]);

  const handleExternalSelectionChange = useCallback(
    (fileIds: string[], checked: boolean, replaceSelection = false) => {
      if (!matter) return;
      setSelectedFiles((prev) => {
        if (!checked) {
          return prev.filter((file) => !fileIds.includes(file.id));
        }

        if (replaceSelection) {
          return matter.files.filter((file) => fileIds.includes(file.id));
        }

        const prevMap = new Map(prev.map((file) => [file.id, file]));
        const next = [...prev];
        fileIds.forEach((fileId) => {
          if (prevMap.has(fileId)) return;
          const file = matter.files.find((item) => item.id === fileId);
          if (!file) return;
          next.push(file);
        });
        return next;
      });
    },
    [matter],
  );

  const handleAfterCreateFile = (file: MatterFile) => {
    setSelectedFiles((prevSelectedFiles) => {
      if (prevSelectedFiles.some((f) => f.id === file.id)) {
        return prevSelectedFiles;
      }

      return [...prevSelectedFiles, file];
    });
  };

  useEffect(() => {
    if (!open) {
      setSelectedFiles([]);
    }
  }, [open]);

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title="Create file reference"
      description="Create a new file reference to the matter"
      size="4xl"
      className="h-[90vh] max-w-7xl"
      loading={isCreating}
      isDirty={selectedFiles.length > 0}
      bodyContained
      bodyClassName="min-h-0 px-0"
      footer={
        <div className="flex justify-end gap-x-2">
          <FormCancelButton
            setOpen={setOpen}
            isDirty={selectedFiles.length > 0}
          />
          <Button
            type="button"
            onClick={() => {
              handleCreateMatterFileReference(selectedFiles);
            }}
            disabled={isCreating || selectedFiles.length === 0}
          >
            {isCreating ? <LoadingSpinner /> : `Save (${selectedFiles.length})`}
          </Button>
        </div>
      }
    >
      <div className="flex h-full min-h-[480px] flex-col overflow-hidden">
        {matter && open && (
          <div className="flex h-full min-h-0 flex-1 overflow-hidden">
            <Suspense
              fallback={
                <div className="bg-card flex h-full min-h-0 w-full items-center justify-center px-6 py-10">
                  <LoadingSpinner className="h-6 w-6" />
                </div>
              }
            >
              <FileRepository
                matter={matter}
                useExternalSelection
                onExternalSelectionChange={handleExternalSelectionChange}
                selectedFiles={selectedFiles}
                showMergeDocuments={false}
                showShareFolders={false}
                autoOpenNewFileUpload={autoOpenNewFileUpload}
                afterCreateFile={handleAfterCreateFile}
                showSelectionActions={false}
                enableTreeDrag={false}
              />
            </Suspense>
          </div>
        )}
      </div>
    </AppModal>
  );
};
